/**
 * AdminPayments — Finance / Admin review queue for offline (EasyPaisa) payments.
 * Route: /app/admin/payments
 *
 * Lists payments by status, shows screenshot preview, and lets the reviewer
 * APPROVE / REJECT with an optional note. Approval activates the user's
 * subscription identically to the Stripe webhook.
 */
import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  CheckCircle2, XCircle, Search, Image as ImageIcon, FileText, Loader2, ChevronRight, RefreshCw,
} from "lucide-react";

const STATUSES = ["PENDING_VERIFICATION", "APPROVED", "REJECTED"];
const API_BASE = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

export default function AdminPayments() {
  const [status, setStatus] = useState("PENDING_VERIFICATION");
  const [search, setSearch] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [reviewBusy, setReviewBusy] = useState(false);
  const [note, setNote] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/manual-payments", { params: { status } });
      setItems(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load payments");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const filtered = items.filter((p) => {
    if (!search.trim()) return true;
    const s = search.toLowerCase();
    return [p.full_name, p.email, p.phone, p.reference, p.sender_account]
      .filter(Boolean).some((v) => String(v).toLowerCase().includes(s));
  });

  const screenshotUrl = (id) =>
    `${API_BASE}/admin/manual-payments/${id}/screenshot?token=${encodeURIComponent(localStorage.getItem("lingoq_token") || "")}`;
  // NOTE: token in query is for the embedded <img> only — admin pages already
  // authed via Authorization header for the JSON list. Backend ignores ?token=
  // and uses cookie/header; if you change auth, update this fetch helper too.

  const review = async (action) => {
    if (!selected) return;
    setReviewBusy(true);
    try {
      await api.patch(`/admin/manual-payments/${selected.id}`, {
        status: action, admin_note: note.trim() || null,
      });
      toast.success(`Payment ${action.toLowerCase()}`);
      setSelected(null); setNote("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    } finally { setReviewBusy(false); }
  };

  return (
    <div className="min-h-screen px-6 py-8 max-w-7xl mx-auto" data-testid="admin-payments-root">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="font-mono text-[10px] tracking-[0.3em] uppercase opacity-60">// FINANCE · MANUAL PAYMENTS</div>
          <h1 className="text-3xl font-bold mt-1">EasyPaisa & manual transfers</h1>
        </div>
        <button onClick={load} className="inline-flex items-center gap-2 px-4 py-2 rounded-sm border" data-testid="admin-payments-refresh">
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        {STATUSES.map((s) => (
          <button key={s} onClick={() => setStatus(s)}
                  data-testid={`payments-filter-${s.toLowerCase()}`}
                  className="px-3 py-1.5 rounded-sm font-mono text-xs tracking-[0.2em] uppercase"
                  style={{ background: status === s ? "#F77F00" : "transparent",
                           color: status === s ? "#000" : undefined,
                           border: status === s ? "1px solid #F77F00" : "1px solid rgba(0,0,0,0.15)" }}>
            {s.replace(/_/g, " ")}
          </button>
        ))}
        <div className="flex items-center gap-2 px-3 py-1.5 rounded-sm border ml-auto" style={{ borderColor: "rgba(0,0,0,0.15)" }}>
          <Search className="w-4 h-4 opacity-60" />
          <input value={search} onChange={(e) => setSearch(e.target.value)}
                 placeholder="Search name / email / phone / TID"
                 data-testid="payments-search"
                 className="outline-none bg-transparent w-72 text-sm" />
        </div>
      </div>

      {/* List */}
      <div className="rounded-md overflow-hidden border" style={{ borderColor: "rgba(0,0,0,0.1)" }} data-testid="payments-list">
        <table className="w-full">
          <thead style={{ background: "rgba(0,0,0,0.04)" }}>
            <tr className="text-left text-xs uppercase tracking-[0.2em]">
              <th className="px-4 py-3">Date</th>
              <th className="px-4 py-3">Candidate</th>
              <th className="px-4 py-3">Plan</th>
              <th className="px-4 py-3">Amount</th>
              <th className="px-4 py-3">Reference</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3 w-10"></th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={7} className="px-4 py-10 text-center">
                <Loader2 className="w-6 h-6 animate-spin inline" /> Loading…
              </td></tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-10 text-center opacity-60" data-testid="payments-empty">
                No {status.toLowerCase().replace(/_/g, " ")} payments.
              </td></tr>
            )}
            {!loading && filtered.map((p) => (
              <tr key={p.id} className="border-t hover:bg-black/[0.03] cursor-pointer"
                  style={{ borderColor: "rgba(0,0,0,0.05)" }}
                  onClick={() => { setSelected(p); setNote(""); }}
                  data-testid={`payment-row-${p.id}`}>
                <td className="px-4 py-3 text-sm whitespace-nowrap">{new Date(p.created_at).toLocaleString()}</td>
                <td className="px-4 py-3">
                  <div className="font-medium">{p.full_name}</div>
                  <div className="text-xs opacity-60">{p.email} · {p.phone}</div>
                </td>
                <td className="px-4 py-3 text-sm">{p.plan_name || p.plan_id}</td>
                <td className="px-4 py-3 font-mono text-sm">{p.currency} {p.amount?.toLocaleString?.()}</td>
                <td className="px-4 py-3 font-mono text-xs">{p.reference}</td>
                <td className="px-4 py-3">
                  <StatusPill status={p.status} />
                </td>
                <td className="px-4 py-3"><ChevronRight className="w-4 h-4 opacity-50" /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Detail drawer */}
      {selected && (
        <div className="fixed inset-0 z-50 flex" style={{ background: "rgba(0,0,0,0.55)" }} onClick={() => setSelected(null)} data-testid="payment-detail">
          <div className="ml-auto w-full max-w-2xl h-full overflow-y-auto bg-white p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] opacity-60">// PAYMENT</div>
                <h2 className="text-2xl font-bold">{selected.full_name}</h2>
                <div className="text-sm opacity-70">{selected.email} · {selected.phone}</div>
              </div>
              <StatusPill status={selected.status} />
            </div>

            <div className="grid grid-cols-2 gap-3 text-sm mb-4">
              <Info label="Plan" value={selected.plan_name || selected.plan_id} />
              <Info label="Amount" value={`${selected.currency} ${selected.amount?.toLocaleString?.()}`} />
              <Info label="Reference / TID" value={selected.reference} mono />
              <Info label="Sender account" value={selected.sender_account || "—"} mono />
              <Info label="Country" value={selected.country_code} />
              <Info label="Submitted" value={new Date(selected.created_at).toLocaleString()} />
            </div>

            {selected.note && (
              <div className="rounded-sm p-3 mb-4 text-sm" style={{ background: "rgba(247,127,0,0.06)", border: "1px solid rgba(247,127,0,0.25)" }}>
                <div className="text-[10px] uppercase tracking-[0.3em] opacity-60 mb-1">CANDIDATE NOTE</div>
                {selected.note}
              </div>
            )}

            <ScreenshotPreview paymentId={selected.id} mime={selected.screenshot_mime} url={screenshotUrl(selected.id)} />

            {selected.status === "PENDING_VERIFICATION" && (
              <div className="mt-5">
                <label className="block text-[10px] uppercase tracking-[0.3em] opacity-60 mb-1.5">ADMIN NOTE (optional)</label>
                <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2}
                          placeholder="e.g. Verified TID against EasyPaisa portal"
                          data-testid="payment-admin-note"
                          className="w-full px-3 py-2 rounded-sm border text-sm outline-none"
                          style={{ borderColor: "rgba(0,0,0,0.15)" }} />
                <div className="flex gap-2 mt-3">
                  <button onClick={() => review("REJECTED")} disabled={reviewBusy} data-testid="payment-reject"
                          className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-sm font-mono text-xs tracking-[0.25em] uppercase border-2"
                          style={{ borderColor: "#D62828", color: "#D62828" }}>
                    {reviewBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <XCircle className="w-4 h-4" />} Reject
                  </button>
                  <button onClick={() => review("APPROVED")} disabled={reviewBusy} data-testid="payment-approve"
                          className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-sm font-mono text-xs tracking-[0.25em] uppercase"
                          style={{ background: "#16A34A", color: "#fff" }}>
                    {reviewBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Approve & activate
                  </button>
                </div>
              </div>
            )}

            {selected.status !== "PENDING_VERIFICATION" && (
              <div className="mt-5 rounded-sm p-3 text-sm"
                   style={{ background: selected.status === "APPROVED" ? "rgba(22,163,74,0.08)" : "rgba(214,40,40,0.08)",
                            border: `1px solid ${selected.status === "APPROVED" ? "#16A34A" : "#D62828"}` }}>
                <div className="text-[10px] uppercase tracking-[0.3em] opacity-60 mb-1">REVIEWED</div>
                {selected.admin_note || "No note left."}
                <div className="text-xs opacity-60 mt-1">{new Date(selected.reviewed_at).toLocaleString()}</div>
              </div>
            )}

            <button onClick={() => setSelected(null)}
                    className="mt-4 w-full px-4 py-2 rounded-sm text-xs uppercase tracking-[0.25em] border"
                    style={{ borderColor: "rgba(0,0,0,0.15)" }} data-testid="payment-close">
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    PENDING_VERIFICATION: { bg: "rgba(247,127,0,0.12)", fg: "#B45309", label: "PENDING" },
    APPROVED: { bg: "rgba(22,163,74,0.12)", fg: "#16A34A", label: "APPROVED" },
    REJECTED: { bg: "rgba(214,40,40,0.10)", fg: "#D62828", label: "REJECTED" },
  };
  const s = map[status] || map.PENDING_VERIFICATION;
  return (
    <span className="inline-flex items-center px-2.5 py-1 rounded-full font-mono text-[10px] tracking-[0.25em]"
          style={{ background: s.bg, color: s.fg }}>{s.label}</span>
  );
}

function Info({ label, value, mono }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.3em] opacity-60 mb-0.5">{label}</div>
      <div className={mono ? "font-mono text-sm" : "text-sm"}>{value}</div>
    </div>
  );
}

function ScreenshotPreview({ paymentId, mime, url }) {
  const [src, setSrc] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Fetch via axios so we send Authorization header — convert blob → URL.
        const res = await api.get(`/admin/manual-payments/${paymentId}/screenshot`, { responseType: "blob" });
        if (cancelled) return;
        const obj = URL.createObjectURL(res.data);
        setSrc(obj);
      } catch (_e) { if (!cancelled) setErr(true); }
    })();
    return () => { cancelled = true; if (src) URL.revokeObjectURL(src); };
    // eslint-disable-next-line
  }, [paymentId]);

  if (err) {
    return (
      <div className="rounded-sm p-4 text-center text-sm opacity-60"
           style={{ background: "rgba(0,0,0,0.04)", border: "1px dashed rgba(0,0,0,0.2)" }}>
        <ImageIcon className="w-6 h-6 mx-auto mb-1 opacity-50" />
        Could not load screenshot.
      </div>
    );
  }
  if (!src) {
    return (
      <div className="rounded-sm p-8 text-center text-sm opacity-60"
           style={{ background: "rgba(0,0,0,0.04)", border: "1px dashed rgba(0,0,0,0.2)" }}>
        <Loader2 className="w-6 h-6 animate-spin mx-auto" />
      </div>
    );
  }
  if ((mime || "").startsWith("image/")) {
    return (
      <a href={src} target="_blank" rel="noreferrer" data-testid="payment-screenshot">
        <img src={src} alt="Payment screenshot"
             className="w-full rounded-sm border" style={{ borderColor: "rgba(0,0,0,0.15)", maxHeight: 480, objectFit: "contain" }} />
      </a>
    );
  }
  return (
    <a href={src} target="_blank" rel="noreferrer" data-testid="payment-screenshot"
       className="flex items-center gap-3 p-4 rounded-sm border" style={{ borderColor: "rgba(0,0,0,0.15)" }}>
      <FileText className="w-8 h-8" /> Open attached PDF
    </a>
  );
}
